import React, { useMemo, useState, useEffect, useCallback } from "react";
import classnames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import Typography from "@vahak/core-ui/dist/components/Typography/Typography";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { scrollToElementById } from "@vahak/core-ui/dist/methods";
import { AppContext } from "@vahak/core/dist/app-context";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { UserScreenId } from "@vahak/core/dist/constants/userScreenId";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
import VahakCircleBlueLogo from "@vahak/core/dist/icons/VahakCircleBlueLogo.svg";
import LoginIcon from "@vahak/core/dist/icons/profile.svg";

import ContinueSignup from "../continue-sign-up/ContinueSignup";
import ProfileItemPopupMenu from "../top-nav-new/profile-item-pop-menu/ProfileItemPopupMenu";
import SmartAppBanner from "./SmartAppBanner";
import { APP_DOWNLOAD_BANNER_SECTION_ID } from "../../homepage-v2/app-download-banner/AppDownloadBannerV2";
import { COMPACT_APP_DOWNLOAD } from "../../homepage-v2/compactAppDownload.constants";
import { MARKETPLACE_ROUTE_CONFIG } from "../../../constants/marketplace.constants";
import { IDs } from "@vahak/core/dist/constants/automation";
import useLoadI18nNamespaces from "../../../hooks/useLoadI18nNamespaces";
import useOpenNewLoadPosting from "../../../hooks/useOpenNewLoadPosting";
import { POSTING_FORM_MODAL_ORIGINATION_PAGE } from "../../../context/load-lorry-posting";
import { WEB_V2_SHELL_LOCALIZATION_NAMESPACES } from "../../../methods/getRouteLocalizationNamespaces";
import Styles from "./TopNavV2.module.scss";

const GA_FROM = "homepage_v2_top_nav";

interface NavLinkConfig {
    id: string;
    label: string;
    isActive?: boolean;
    onClick: () => void;
}

const TopNavV2 = () => {
    const { isPhoneVerified, companyId } = React.useContext(AppContext);
    const router = useRouter();
    const { t } = useTranslation("navigation");
    useLoadI18nNamespaces(WEB_V2_SHELL_LOCALIZATION_NAMESPACES);
    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const openNewLoadPosting = useOpenNewLoadPosting();
    const isDesktop = useMediaQuery({
        queryType: "desktop",
        initialState: typeof window !== "undefined" ? window.matchMedia("(min-width: 992px)").matches : false
    });

    const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    // TEMP: language change hidden
    // const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

    useEffect(() => {
        const closeDrawerOnNavigate = () => setIsHamburgerMenuOpen(false);
        router.events.on("routeChangeStart", closeDrawerOnNavigate);
        return () => {
            router.events.off("routeChangeStart", closeDrawerOnNavigate);
        };
    }, [router.events]);

    const isHomepage = router.pathname === "/";
    const isCargoInsurancePage = router.pathname === UserScreenId.cargoInsurance.pathname;
    const isMembershipPage = router.pathname === ROUTES.membershipUpgrade.getUrl();
    const isMarketplaceLoadPage =
        router.pathname === "/marketplace/[...params]" && router.asPath.includes(MARKETPLACE_ROUTE_CONFIG.loadUrl);
    const hasDarkHero = isHomepage || isCargoInsurancePage || isMembershipPage || isMarketplaceLoadPage;

    useEffect(() => {
        if (!hasDarkHero) {
            setIsScrolled(false);
            return;
        }

        const updateScrolledState = () => setIsScrolled(window.scrollY > 24);
        updateScrolledState();
        window.addEventListener("scroll", updateScrolledState, { passive: true });
        return () => window.removeEventListener("scroll", updateScrolledState);
    }, [hasDarkHero]);

    // TEMP: language change hidden
    // const PageLanguages = getNavLanguagesForRoute(router.pathname, router.asPath);

    const trackEvent = (eventName: string, extra?: Record<string, unknown>) => {
        sendGAandMoEngageEvent({
            name: eventName,
            data: {
                from_screen: router.asPath,
                from: GA_FROM,
                ...extra
            }
        });
    };

    const resetMenuState = () => {
        setIsHamburgerMenuOpen(false);
        // TEMP: language change hidden
        // setIsLanguageMenuOpen(false);
    };

    const handleRouteNav = (pathname: string, eventName: string) => {
        trackEvent(eventName);
        resetMenuState();
        router.push(pathname);
    };

    const handleLogoClick = () => {
        resetMenuState();
        if (companyId) {
            if (router.pathname.includes("/dashboard")) {
                return;
            }
            router.push(ROUTES.dashboard.getUrl());
            return;
        }
        if (isHomepage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        router.push("/");
    };

    const handleLogin = () => {
        trackEvent(GA4EventNames.WEB_V2.title_login_signup);
        resetMenuState();
        router.push({ hash: "#Login" });
    };

    const handleDownloadClick = () => {
        trackEvent(GA4EventNames.WEB_V2.title_download_app_clicked);
        resetMenuState();

        if (scrollToElementById(APP_DOWNLOAD_BANNER_SECTION_ID)) {
            return;
        }

        window.open(COMPACT_APP_DOWNLOAD.playStoreUrl, "_blank", "noopener,noreferrer");
    };

    const getTopNavSourceScreen = useCallback(() => {
        if (isHomepage) {
            return WEB_V2_SOURCE_SCREENS.HOME_PAGE;
        }
        if (isMarketplaceLoadPage) {
            return WEB_V2_SOURCE_SCREENS.MARKETPLACE;
        }
        if (isMembershipPage) {
            return WEB_V2_SOURCE_SCREENS.MEMBERSHIP;
        }
        if (isCargoInsurancePage) {
            return WEB_V2_SOURCE_SCREENS.CARGO_INSURANCE;
        }

        return WEB_V2_SOURCE_SCREENS.HOME_PAGE;
    }, [isCargoInsurancePage, isHomepage, isMarketplaceLoadPage, isMembershipPage]);

    const handleFindLorry = useCallback(() => {
        trackEvent(GA4EventNames.WEB_V2.title_find_lorry_clicked);
        resetMenuState();
        openNewLoadPosting({
            originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.HOME,
            eventData: { source_screen: getTopNavSourceScreen() }
        });
    }, [getTopNavSourceScreen, openNewLoadPosting]);

    const navLinks = useMemo<NavLinkConfig[]>(
        () => [
            {
                id: "find-lorry",
                label: t("findLorry"),
                onClick: handleFindLorry
            },
            {
                id: "find-loads",
                label: t("findLoads"),
                isActive: isMarketplaceLoadPage,
                onClick: () =>
                    handleRouteNav(MARKETPLACE_ROUTE_CONFIG.loadUrl, GA4EventNames.WEB_V2.title_find_loads_clicked)
            },
            {
                id: "download-app",
                label: t("downloadApp"),
                onClick: handleDownloadClick
            },
            {
                id: "membership",
                label: t("membership"),
                isActive: isMembershipPage,
                onClick: () =>
                    handleRouteNav(ROUTES.membershipUpgrade.getUrl(), GA4EventNames.WEB_V2.title_membership_clicked)
            },
            {
                id: "cargo-insurance",
                label: t("cargoInsurance"),
                isActive: isCargoInsurancePage,
                onClick: () => {
                    if (isCargoInsurancePage) {
                        resetMenuState();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        return;
                    }
                    handleRouteNav(
                        UserScreenId.cargoInsurance.pathname,
                        GA4EventNames.WEB_V2.title_cargo_insurance_clicked
                    );
                }
            }
        ],
        [handleFindLorry, isCargoInsurancePage, isMarketplaceLoadPage, isMembershipPage, t]
    );

    // TEMP: language change hidden
    /*
    const renderLangList = () => (
        <ul className={Styles.languageSubmenuWrapper}>
            {PageLanguages?.languages?.map((language) => (
                <li
                    key={language.id}
                    className={classnames(Styles.languageSubmenu, (lang || 0) === language.id && Styles.active)}
                    onClick={() => {
                        trackEvent(GA4EventNames.WEB_V2.title_language_change, {
                            language: language.value
                        });
                        setLang?.(language.id);
                        resetMenuState();
                    }}
                >
                    <Typography weight="medium" size="sm">
                        {language.labelInLanguage}
                    </Typography>
                    <Typography weight="medium" size={language.labelInEn === "English" ? "sm" : "xs"}>
                        {language.labelInEn}
                    </Typography>
                </li>
            ))}
        </ul>
    );
    */

    const renderNavLink = (link: NavLinkConfig, className: string) => (
        <button key={link.id} type="button" className={className} onClick={link.onClick}>
            {link.label}
        </button>
    );

    const renderAuthControl = () => {
        if (isPhoneVerified) {
            return (
                <div className={Styles.profileSlot} id={IDs.profileDropDown.profileDropdownBtn}>
                    <ProfileItemPopupMenu onClickMenuCallBack={resetMenuState} useBottomSheetMenu={!isDesktop} />
                </div>
            );
        }

        return (
            <>
                <div className={Styles.authActions}>
                    <button type="button" className={Styles.loginBtn} onClick={handleLogin}>
                        {t("login")}
                    </button>
                </div>
                <button type="button" className={Styles.mobileLoginBtn} onClick={handleLogin}>
                    <LoginIcon />
                    <span>{t("login")}</span>
                </button>
            </>
        );
    };

    return (
        <header
            className={classnames(
                Styles.main,
                hasDarkHero && Styles.darkHeroMain,
                hasDarkHero && isScrolled && Styles.darkHeroMainScrolled
            )}
        >
            <SmartAppBanner
                onDownload={(store) =>
                    trackEvent(
                        store === "app_store"
                            ? GA4EventNames.WEB_V2.download_appstore
                            : GA4EventNames.WEB_V2.download_playstore
                    )
                }
            />
            <ContinueSignup />
            <div className={Styles.inner}>
                <div className={Styles.left}>
                    <div
                        className={classnames(Styles.hamburgerMenu, isHamburgerMenuOpen && Styles.open)}
                        onClick={() => setIsHamburgerMenuOpen(!isHamburgerMenuOpen)}
                        role="button"
                        tabIndex={0}
                        aria-label="Toggle menu"
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setIsHamburgerMenuOpen(!isHamburgerMenuOpen);
                            }
                        }}
                    >
                        <span />
                        <span />
                        <span />
                    </div>
                    <div
                        className={Styles.logoWrapper}
                        id={IDs.common.vahakLogo}
                        onClick={handleLogoClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleLogoClick();
                            }
                        }}
                    >
                        <VahakCircleBlueLogo />
                        <span className={Styles.logoText}>Vahak</span>
                    </div>
                </div>

                <nav className={Styles.centerNav} aria-label="Primary">
                    {navLinks.map((link) =>
                        renderNavLink(link, classnames(Styles.navLink, link.isActive && Styles.navLinkActive))
                    )}
                </nav>

                <div className={Styles.right}>
                    {renderAuthControl()}
                    {/* TEMP: language change hidden
                    {isDesktop && PageLanguages && (
                        <Dropdown
                            isOpen={isLanguageMenuOpen}
                            containerClassName={Styles.languageDropdownContainer}
                            DropdownToggleBtn={
                                <div className={Styles.langIconWrapper}>
                                    <LanguageIcon />
                                </div>
                            }
                            handleToggle={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                            dropdownClassName={Styles.languageDropdown}
                            dropdownToggleClassName={Styles.languageDropdownToggle}
                        >
                            {renderLangList()}
                        </Dropdown>
                    )}
                    */}
                </div>
            </div>

            <div className={classnames(Styles.appDrawMenu, isHamburgerMenuOpen && Styles.isOpen)}>
                <div className={Styles.appDrawMenuOptionListWrapper}>
                    {navLinks.map((link) => (
                        <div key={link.id} className={Styles.appDrawMenuOption}>
                            <button
                                type="button"
                                className={classnames(Styles.drawerLink, link.isActive && Styles.drawerLinkActive)}
                                onClick={link.onClick}
                            >
                                <Typography color={COLORS.BLACK} weight="semibold" mSize="sm">
                                    {link.label}
                                </Typography>
                            </button>
                        </div>
                    ))}

                    {/* TEMP: language change hidden
                        {PageLanguages && (
                            <div className={Styles.appDrawMenuOption}>
                                <button
                                    type="button"
                                    className={Styles.drawerLink}
                                    onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                                >
                                    <Typography color={COLORS.BLACK} weight="semibold" mSize="sm">
                                        {t("changeLanguage")} {isLanguageMenuOpen ? <AngleUpBlue /> : <AngleDownBlue />}
                                    </Typography>
                                </button>
                                {isLanguageMenuOpen ? renderLangList() : null}
                            </div>
                        )}
                        */}

                    {!isPhoneVerified ? (
                        <div className={Styles.drawerActions}>
                            <button type="button" className={Styles.drawerLoginBtn} onClick={handleLogin}>
                                {t("login")}
                            </button>
                            <button type="button" className={Styles.drawerSignUpBtn} onClick={handleLogin}>
                                {t("signUp")}
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </header>
    );
};

export default TopNavV2;
